import type {
  IAuthService,
  AuthLocalSessionClearOptions,
  AuthLogoutExpectation,
  AuthLogoutResult,
  AuthLogoutScope,
  AuthSession,
  AuthSessionGeneration,
  AuthSessionTokens,
  AuthStateChangeMetadata,
  AuthUser,
  LoginCredentials,
  PlatformAdminTenantViewAccess,
  ProSignupCredentials,
  ProSignupErrorCode,
  SignupCredentials,
  StorefrontEditorHandoff,
  ProPlatformHandoff,
  ProPlatformHandoffKind,
  ProPlatformPreviewSession,
  StorefrontSignupErrorCode,
} from '../../interfaces/src';
import type { SupabaseClientOptions } from '@supabase/supabase-js';
import { getSupabaseClient } from './client';

const TOKEN_REFRESH_WINDOW_MS = 60_000;
export const AUTH_SIGN_OUT_TIMEOUT_MS = 10_000;
export const AUTH_LOCAL_SESSION_CLEAR_TIMEOUT_MS = 5_000;

export type PasswordRecoveryCallback =
  | { kind: 'implicit'; accessToken: string; refreshToken: string }
  | { kind: 'pkce'; code: string }
  | {
      kind: 'error';
      code: 'password_recovery_link_expired' | 'password_recovery_link_invalid';
    };

/**
 * Parse only callback shapes that belong to the password-recovery route.
 * Tokens can arrive in the fragment (implicit flow), while PKCE returns a
 * one-time `code` in the query string. The provider error description is
 * intentionally discarded so internal/auth-server text never reaches UI.
 */
export function parsePasswordRecoveryCallback(
  rawUrl: string,
): PasswordRecoveryCallback | null {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return null;
  }

  const fragment = new URLSearchParams(
    parsed.hash.startsWith('#') ? parsed.hash.slice(1) : parsed.hash,
  );
  const read = (key: string): string | null =>
    fragment.get(key) ?? parsed.searchParams.get(key);
  const recoveryRoute =
    parsed.hostname.toLowerCase() === 'update-password' ||
    parsed.pathname.toLowerCase().endsWith('/update-password');
  const recoveryType = read('type') === 'recovery';
  const providerError = read('error_code') ?? read('error');

  if (!recoveryRoute && !recoveryType) return null;
  if (providerError) {
    return {
      kind: 'error',
      code: providerError.toLowerCase().includes('expired')
        ? 'password_recovery_link_expired'
        : 'password_recovery_link_invalid',
    };
  }

  const accessToken = read('access_token');
  const refreshToken = read('refresh_token');
  if (accessToken && refreshToken) {
    return { kind: 'implicit', accessToken, refreshToken };
  }

  const code = parsed.searchParams.get('code');
  if (code) return { kind: 'pkce', code };

  return { kind: 'error', code: 'password_recovery_link_invalid' };
}

interface ConfirmableAuthStorage {
  readonly supportsOfflineSessionRestore?: boolean;
  getItem(key: string): string | null | Promise<string | null>;
  removeItem(key: string): void | Promise<void>;
}

type AuthStorageLock = <T>(
  name: string,
  acquireTimeout: number,
  operation: () => Promise<T>,
) => Promise<T>;

interface AuthStorageInternals {
  storage?: ConfirmableAuthStorage;
  userStorage?: ConfirmableAuthStorage | null;
  storageKey?: string;
  lock?: AuthStorageLock;
  /** auth-js network primitive; unlike refreshSession it does not reacquire
   * the already-held storage lock or persist the rotated tokens. */
  _refreshAccessToken?: (refreshToken: string) => Promise<{
    data: {
      session: { access_token?: string } | null;
      user: unknown | null;
    };
    error: unknown | null;
  }>;
}

interface SessionStorageFence {
  /** Always settles successfully, but only after the latest operation settles. */
  tail: Promise<void>;
}

interface AuthStateSubscriber {
  callback: (user: AuthUser | null, metadata?: AuthStateChangeMetadata) => void;
  lastSnapshot?: string;
}

// All cached GoTrue clients in a realm normally share the same storage object.
// Keeping the fence by storage identity makes a timed-out removal remain a
// real barrier: the public call may reject after five seconds, but a fresh
// login cannot overtake the still-running storage bridge operation.
const sessionStorageFences = new WeakMap<object, SessionStorageFence>();

function enqueueSessionStorageOperation<T>(
  storage: ConfirmableAuthStorage,
  operation: () => Promise<T>,
): Promise<T> {
  let fence = sessionStorageFences.get(storage);
  if (!fence) {
    fence = { tail: Promise.resolve() };
    sessionStorageFences.set(storage, fence);
  }

  const result = fence.tail.then(operation, operation);
  fence.tail = result.then(
    () => undefined,
    () => undefined,
  );
  return result;
}

function runWithAuthStorageLock<T>(
  auth: AuthStorageInternals,
  operation: () => Promise<T>,
): Promise<T> {
  if (!auth.lock || !auth.storageKey) return operation();
  // Negative timeout is intentional. In auth-js 2.103 a positive timeout
  // steals the Web Lock while the prior callback continues without exclusive
  // access. Waiting lets an already-running refresh finish first; our clear
  // then removes the session it may have written.
  return auth.lock(`lock:${auth.storageKey}`, -1, operation);
}

async function withTimeout<T>(
  operation: Promise<T>,
  timeoutMs: number,
  timeoutCode: string,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      operation,
      new Promise<never>((_resolve, reject) => {
        timer = globalThis.setTimeout(
          () => reject(new Error(timeoutCode)),
          timeoutMs,
        );
      }),
    ]);
  } finally {
    if (timer !== undefined) globalThis.clearTimeout(timer);
  }
}

async function removeAndConfirm(
  storage: ConfirmableAuthStorage,
  keys: readonly string[],
): Promise<void> {
  const removals = await Promise.allSettled(
    keys.map((key) => Promise.resolve(storage.removeItem(key))),
  );
  const remaining: string[] = [];
  let readFailure: unknown;
  for (const key of keys) {
    try {
      if ((await storage.getItem(key)) !== null) remaining.push(key);
    } catch (error) {
      readFailure ??= error;
    }
  }
  if (remaining.length === 0 && readFailure === undefined) return;

  const removalFailure = removals.find(
    (result): result is PromiseRejectedResult => result.status === 'rejected',
  );
  throw Object.assign(new Error('local_session_clear_failed'), {
    cause: readFailure ?? removalFailure?.reason,
  });
}

function readPersistedAccessToken(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const persisted = JSON.parse(raw) as {
      access_token?: unknown;
      currentSession?: { access_token?: unknown };
    } | null;
    const token =
      persisted?.access_token ?? persisted?.currentSession?.access_token;
    return typeof token === 'string' && token.length > 0 ? token : null;
  } catch {
    return null;
  }
}

function readPersistedRefreshToken(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const persisted = JSON.parse(raw) as {
      refresh_token?: unknown;
      currentSession?: { refresh_token?: unknown };
    } | null;
    const token =
      persisted?.refresh_token ?? persisted?.currentSession?.refresh_token;
    return typeof token === 'string' && token.length > 0 ? token : null;
  } catch {
    return null;
  }
}

interface AuthTokenIdentity {
  userId: string | null;
  sessionGeneration: AuthSessionGeneration | null;
}

function readAuthTokenIdentity(accessToken: string | null): AuthTokenIdentity {
  const empty: AuthTokenIdentity = { userId: null, sessionGeneration: null };
  if (!accessToken) return empty;
  const payloadSegment = accessToken.split('.')[1];
  if (!payloadSegment || typeof globalThis.atob !== 'function') return empty;
  try {
    const normalized = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      '=',
    );
    const binary = globalThis.atob(padded);
    const bytes = Uint8Array.from(binary, (character) =>
      character.charCodeAt(0),
    );
    const payload = JSON.parse(new TextDecoder().decode(bytes)) as Record<
      string,
      unknown
    >;
    const subject = payload['sub'];
    const sessionId = payload['session_id'];
    // `session_id` is the server-issued session generation: refreshes keep it,
    // while every real sign-in creates a new auth.sessions row/id. It is not a
    // bearer credential, and the prefix keeps the boundary value opaque to
    // consumers that must never interpret Supabase JWT claims themselves.
    return {
      userId:
        typeof subject === 'string' && subject.length > 0 ? subject : null,
      sessionGeneration:
        typeof sessionId === 'string' && sessionId.length > 0
          ? `supabase-session:${sessionId}`
          : null,
    };
  } catch {
    return empty;
  }
}

function readSessionGenerationFromAccessToken(
  accessToken: string | null,
): AuthSessionGeneration | null {
  return readAuthTokenIdentity(accessToken).sessionGeneration;
}

function readPersistedSessionGeneration(
  raw: string | null,
): AuthSessionGeneration | null {
  return readSessionGenerationFromAccessToken(readPersistedAccessToken(raw));
}

function isErrorCode(error: unknown, code: string): boolean {
  return error instanceof Error && error.message === code;
}

function isTerminalGlobalRevocationError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const authError = error as { name?: unknown; status?: unknown };
  if (authError.name === 'AuthSessionMissingError') return true;
  return (
    authError.name === 'AuthApiError' &&
    (authError.status === 401 ||
      authError.status === 403 ||
      authError.status === 404)
  );
}

function authUserSnapshot(
  user: AuthUser | null,
  sessionGeneration?: AuthSessionGeneration | null,
): string {
  if (!user) return 'signed-out';
  return JSON.stringify([
    user.id,
    user.email,
    user.role,
    user.displayName,
    user.phone,
    user.pendingEmail,
    sessionGeneration,
  ]);
}

export class SupabaseAuthService implements IAuthService {
  private supabase;
  private authSupabase;
  private readonly supportsOfflineSessionRestore: boolean;
  private readonly authStateSubscribers = new Set<AuthStateSubscriber>();

  constructor(
    supabaseUrl: string,
    supabaseAnonKey: string,
    options?: SupabaseClientOptions<'public'>,
    private readonly externalAccessToken?: string,
  ) {
    this.supportsOfflineSessionRestore =
      (
        options?.auth?.storage as ConfirmableAuthStorage | undefined
      )?.supportsOfflineSessionRestore === true;
    this.supabase = getSupabaseClient(supabaseUrl, supabaseAnonKey, options);
    this.authSupabase = getSupabaseClient(
      supabaseUrl,
      supabaseAnonKey,
      withoutGlobalHeaders(options),
    );
  }

  async login(credentials: LoginCredentials): Promise<AuthSession> {
    return this.runSessionEstablishingOperation(async () => {
      const { data, error } = await this.authSupabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });
      if (error) throw error;
      if (!data.user || !data.session) throw new Error('Login failed');

      // clearLocalSession() stops proactive refresh before deleting storage.
      // A later explicit authentication owns a fresh session and must restart
      // the timer after all older destructive storage work has settled.
      await this.authSupabase.auth.startAutoRefresh();
      return {
        user: {
          id: data.user.id,
          email: data.user.email!,
          role: extractRole(data.user.user_metadata),
          displayName: extractDisplayName(data.user.user_metadata),
          phone: extractPhone(data.user.user_metadata),
        },
        accessToken: data.session.access_token,
        sessionGeneration: readSessionGenerationFromAccessToken(
          data.session.access_token,
        ),
      };
    });
  }

  async signup(credentials: SignupCredentials): Promise<AuthSession | null> {
    return this.runSessionEstablishingOperation(async () => {
      const locale = detectLocale();
      // Metadata stamped on auth.users at creation. The phone is the piece
      // that matters commercially: it survives an abandoned onboarding, so
      // sales can still reach the person (the tenant row — and its phone
      // column — only exists once the wizard completes). The resend flow
      // replays signup() on an existing unconfirmed user: GoTrue ignores
      // the metadata then, which is fine — it was stored on the first call.
      const metadata: Record<string, string> = {};
      if (locale) metadata.locale = locale;
      if (credentials.fullName?.trim()) {
        const fullName = credentials.fullName.trim();
        metadata.full_name = fullName;
        metadata.display_name = fullName;
      }
      if (credentials.phone?.trim()) metadata.phone = credentials.phone.trim();
      const { data, error } = await this.authSupabase.auth.signUp({
        email: credentials.email,
        password: credentials.password,
        options: {
          // Where the confirmation link lands. WITHOUT this Supabase falls back
          // to the project's Site URL (the app root), and a freshly-confirmed
          // user with no tenant depends on ProtectedRoute bouncing them to
          // /welcome — which it can fail to do (e.g. a leftover platform-admin
          // "view as tenant" session in the tab sends /welcome straight to /).
          // Naming the destination makes the landing deterministic: confirm the
          // email, acknowledge success, then continue to onboarding without a
          // second login. `requestPasswordReset` already follows the same pattern.
          ...(typeof window !== 'undefined'
            ? { emailRedirectTo: `${window.location.origin}/auth/confirm` }
            : {}),
          // Stamp the UI language onto the user so the Send-Email auth hook can
          // localise confirm/recovery emails (FR/AR/EN). Falls back to FR when absent.
          ...(Object.keys(metadata).length > 0 ? { data: metadata } : {}),
        },
      });
      if (error) throw error;
      // When email confirmation is enabled (Supabase default), `session` is
      // null and the user must click the link in their inbox before they
      // can log in. With confirmation disabled they're already logged in.
      if (!data.user) throw new Error('Signup failed');
      // Address already registered AND confirmed. Supabase's email-enumeration
      // protection refuses to say so: it returns 200 with an obfuscated user,
      // no session, and sends NO email. Left undetected we show "check your
      // inbox" for a mailbox that will never receive anything — and the resend
      // button cheerfully reports success. An empty `identities` array is the
      // documented tell. Surface it as an error so the UI can offer sign-in.
      if (
        Array.isArray(data.user.identities) &&
        data.user.identities.length === 0
      ) {
        throw Object.assign(new Error('Email already registered'), {
          code: 'email_already_registered' as const,
        });
      }
      await this.authSupabase.auth.startAutoRefresh();
      if (!data.session) return null;
      return {
        user: {
          id: data.user.id,
          email: data.user.email!,
          role: extractRole(data.user.user_metadata),
          displayName: extractDisplayName(data.user.user_metadata),
          phone: extractPhone(data.user.user_metadata),
        },
        accessToken: data.session.access_token,
        sessionGeneration: readSessionGenerationFromAccessToken(
          data.session.access_token,
        ),
      };
    });
  }

  async signupAsPro(
    credentials: ProSignupCredentials,
  ): Promise<AuthSession | null> {
    const locale = detectLocale();
    // Pas `auth.signUp()` : avec `enable_confirmations` ON au niveau du projet
    // il ne renvoie AUCUNE session, donc la ligne `pro_customers` (insérée
    // sous RLS, session obligatoire) n'était jamais créée — l'électricien
    // n'apparaissait pas dans la file d'approbation du fournisseur. L'edge
    // function crée le compte déjà confirmé, côté serveur.
    const { error } = await this.authSupabase.functions.invoke('pro-signup', {
      body: {
        email: credentials.email,
        password: credentials.password,
        firstName: credentials.firstName,
        lastName: credentials.lastName,
        tenantSubdomain: credentials.tenantSubdomain,
        ...(locale ? { locale } : {}),
        metadata: credentials.metadata ?? {},
      },
    });
    if (error) throw await toProSignupError(error);

    // Le compte est utilisable immédiatement : le mot de passe qui vient
    // d'être choisi ouvre la session, sans aller-retour par la boîte mail.
    return this.login({
      email: credentials.email,
      password: credentials.password,
    });
  }

  async signupStorefrontCustomer(
    credentials: LoginCredentials,
    tenantId: string,
  ): Promise<AuthSession> {
    const locale = detectLocale();
    const { error } = await this.authSupabase.functions.invoke(
      'storefront-signup',
      {
        body: {
          email: credentials.email,
          password: credentials.password,
          tenantId,
          ...(locale ? { locale } : {}),
        },
      },
    );
    if (error) throw await toStorefrontSignupError(error);

    // The account is created already confirmed, so the password the customer
    // just chose signs them in on the spot — no inbox round-trip.
    return this.login(credentials);
  }

  async requestPasswordReset(
    email: string,
    redirectTo?: string,
  ): Promise<void> {
    const { error } = await this.authSupabase.auth.resetPasswordForEmail(
      email,
      {
        redirectTo,
      },
    );
    if (error) throw error;
  }

  async consumePasswordRecoveryUrl(url: string): Promise<boolean> {
    if (this.externalAccessToken) return false;
    const callback = parsePasswordRecoveryCallback(url);
    if (!callback) return false;
    if (callback.kind === 'error') throw new Error(callback.code);

    await this.runAfterSessionStorageQuiescence(async () => {
      if (callback.kind === 'pkce') {
        const { error } = await this.authSupabase.auth.exchangeCodeForSession(
          callback.code,
        );
        if (error) throw error;
        return;
      }

      const { error } = await this.authSupabase.auth.setSession({
        access_token: callback.accessToken,
        refresh_token: callback.refreshToken,
      });
      if (error) throw error;
    });
    return true;
  }

  async updatePassword(newPassword: string): Promise<void> {
    await this.runAfterSessionStorageQuiescence(async () => {
      const { error } = await this.authSupabase.auth.updateUser({
        password: newPassword,
      });
      if (error) throw error;
    });
  }

  async deleteAccount(): Promise<void> {
    await this.runAfterSessionStorageQuiescence(async () => {
      type SbAny = {
        rpc: (
          fn: string,
          args: Record<string, unknown>,
        ) => Promise<{ error: { message: string } | null }>;
      };
      const { error } = await (this.authSupabase as unknown as SbAny).rpc(
        'delete_my_account',
        {},
      );
      // Codes bruts (`owner_cannot_delete`, `pro_customer_cannot_delete`) laissés
      // tels quels : c'est l'UI qui les traduit, comme pour les invitations.
      if (error) throw new Error(error.message);
      // La session serveur est morte avec le compte (auth.sessions cascade) ;
      // ce signOut ne fait que nettoyer l'état local et prévenir les listeners.
      await this.authSupabase.auth.signOut().catch(() => undefined);
    });
  }

  async updateEmail(newEmail: string): Promise<void> {
    // Ne bascule PAS l'adresse : GoTrue envoie un lien à l'ancienne ET à la
    // nouvelle, puis remplit `new_email` (cf. extractPendingEmail). Le compte
    // continue de se connecter avec l'ancienne adresse d'ici là.
    await this.runAfterSessionStorageQuiescence(async () => {
      const { error } = await this.authSupabase.auth.updateUser({
        email: newEmail,
      });
      if (error) throw error;
    });
  }

  onPasswordRecovery(
    callback: (
      userId: string | null,
      sessionGeneration: AuthSessionGeneration | null,
    ) => void,
  ): () => void {
    if (this.externalAccessToken) {
      return () => undefined;
    }

    const { data } = this.authSupabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'PASSWORD_RECOVERY') {
          callback(
            session?.user.id ?? null,
            readSessionGenerationFromAccessToken(session?.access_token ?? null),
          );
        }
      },
    );
    return () => data.subscription.unsubscribe();
  }

  /**
   * `local` par défaut — et c'est un changement délibéré : supabase-js
   * applique `global` quand on ne passe rien, si bien qu'une déconnexion sur
   * un poste tuait la session de TOUS les autres appareils du compte. Le
   * multi-appareils (plusieurs desktops ouverts en même temps) exige que
   * chacun ne révoque que son propre refresh token. Le « déconnecter partout »
   * explicite de Paramètres → Sessions passe, lui, `global`.
   */
  async logout(
    scope: AuthLogoutScope = 'local',
    expectedSession?: AuthLogoutExpectation,
  ): Promise<AuthLogoutResult> {
    const auth = this.getAuthStorageInternals();
    if (!auth.storage || !auth.storageKey) {
      throw new Error('local_session_storage_unavailable');
    }

    let settled = false;
    let localSessionCleared = false;
    let partialGlobalFailure: unknown;
    let rejectVisible!: (error: unknown) => void;
    let signedOutSessionGeneration: AuthSessionGeneration | null = null;
    let signedOutUserId: string | null = null;
    let resolveVisibleResult!: (result: AuthLogoutResult) => void;
    const visibleOutcome = new Promise<AuthLogoutResult>((resolve, reject) => {
      resolveVisibleResult = (result) => {
        if (settled) return;
        settled = true;
        resolve(result);
      };
      rejectVisible = (error) => {
        if (settled) return;
        settled = true;
        reject(error);
      };
    });

    const background = enqueueSessionStorageOperation(auth.storage, () =>
      runWithAuthStorageLock(auth, async () => {
        // One critical section spans read -> revoke -> clear. Otherwise a
        // second tab can save account B during the network gap and this tab's
        // later clear will erase B.
        let accessToken: string | null = this.externalAccessToken ?? null;
        let refreshToken: string | null = null;
        let identity = readAuthTokenIdentity(accessToken);
        if (!this.externalAccessToken) {
          const rawRead = Promise.resolve(
            auth.storage!.getItem(auth.storageKey!),
          ).then((raw) => {
            const persistedAccessToken = readPersistedAccessToken(raw);
            return {
              accessToken: persistedAccessToken,
              refreshToken: readPersistedRefreshToken(raw),
              identity: readAuthTokenIdentity(persistedAccessToken),
            };
          });
          try {
            const persisted = await withTimeout(
              rawRead,
              AUTH_LOCAL_SESSION_CLEAR_TIMEOUT_MS,
              'local_session_read_timeout',
            );
            accessToken = persisted.accessToken;
            refreshToken = persisted.refreshToken;
            identity = persisted.identity;
          } catch (error) {
            if (isErrorCode(error, 'local_session_read_timeout')) {
              // Bound the caller, but retain both lock and fence until the
              // native read settles. Do not schedule a destructive clear from
              // an attempt whose read could not be confirmed in time.
              rejectVisible(error);
              await rawRead.catch(() => undefined);
              return;
            }
            if (scope === 'global') {
              rejectVisible(error);
              return;
            }
            // A settled read failure still permits an offline/local clear.
            accessToken = null;
            refreshToken = null;
            identity = readAuthTokenIdentity(null);
          }
        }

        // Bind the destructive operation to the account rendered by the
        // caller. Another tab may have replaced the origin-shared Supabase
        // session before its SIGNED_IN event reaches this React realm. The JWT
        // subject and generation are checked inside the same storage/Web Lock
        // that spans remote revoke and local removal.
        if (
          expectedSession &&
          (identity.userId !== expectedSession.userId ||
            (expectedSession.sessionGeneration !== undefined &&
              identity.sessionGeneration !== expectedSession.sessionGeneration))
        ) {
          rejectVisible(new Error('auth_session_changed'));
          return;
        }
        signedOutUserId = identity.userId;
        signedOutSessionGeneration = identity.sessionGeneration;

        if (accessToken) {
          const remoteRevoke = this.revokeSessionRemotely(accessToken, scope);
          try {
            await withTimeout(
              remoteRevoke,
              AUTH_SIGN_OUT_TIMEOUT_MS,
              'auth_sign_out_timeout',
            );
          } catch (error) {
            if (scope === 'global') {
              // Match auth-js: 401/403/404 and a missing auth session prove
              // that the named server session is already unusable, so local
              // removal is both safe and necessary. Network/5xx/timeout errors
              // do not confirm a server-wide revoke and must fail closed.
              if (!isTerminalGlobalRevocationError(error)) {
                rejectVisible(error);
                // The public admin primitive is network-only and exposes no
                // AbortSignal in auth-js 2.103. If the visible timeout wins,
                // retain the origin lock until that same server-wide revoke
                // settles: releasing it would let a same-account login create
                // a fresh session that the old global request could revoke.
                // Closing/reloading the page still aborts the realm and
                // releases the browser lock if the fetch never settles.
                if (isErrorCode(error, 'auth_sign_out_timeout')) {
                  const lateGlobalRevokeSucceeded = await remoteRevoke
                    .then(() => true)
                    .catch(() => false);
                  if (lateGlobalRevokeSucceeded) {
                    // The caller has already received the bounded timeout,
                    // but the exact server-wide revoke eventually succeeded.
                    // Continue through the generation re-check and local clear
                    // while the old session still owns the auth lock.
                    partialGlobalFailure = undefined;
                  } else {
                    return;
                  }
                } else {
                  return;
                }
              } else {
                // The current JWT is unusable, so its local profile may be
                // removed. Keep the global result partial until a refresh-token
                // retry proves every server session was actually revoked.
                partialGlobalFailure = error;
              }
            }
            if (refreshToken && !isErrorCode(error, 'auth_sign_out_timeout')) {
              let refreshedRemoteRevoke: Promise<void> | undefined;
              try {
                // auth-js normally refreshes an expired access JWT before
                // signOut. Our network-only path avoids its late local-storage
                // mutation, so reproduce only that refresh/revoke portion while
                // retaining the outer cross-tab lock and final identity check.
                const refreshedAccessToken = await withTimeout(
                  this.refreshAccessTokenForLogout(refreshToken),
                  AUTH_SIGN_OUT_TIMEOUT_MS,
                  'auth_logout_refresh_timeout',
                );
                const refreshedIdentity =
                  readAuthTokenIdentity(refreshedAccessToken);
                if (!refreshedAccessToken) {
                  throw new Error('global_session_revoke_unconfirmed');
                }
                if (
                  refreshedIdentity.userId !== signedOutUserId ||
                  refreshedIdentity.sessionGeneration !==
                    signedOutSessionGeneration
                ) {
                  rejectVisible(new Error('auth_session_changed'));
                  return;
                }
                refreshedRemoteRevoke = this.revokeSessionRemotely(
                  refreshedAccessToken,
                  scope,
                );
                await withTimeout(
                  refreshedRemoteRevoke,
                  AUTH_SIGN_OUT_TIMEOUT_MS,
                  'auth_sign_out_timeout',
                );
                if (scope === 'global') partialGlobalFailure = undefined;
              } catch (refreshError) {
                if (isErrorCode(refreshError, 'auth_session_changed')) {
                  rejectVisible(refreshError);
                  return;
                }
                if (scope === 'global') partialGlobalFailure = refreshError;
                if (
                  scope === 'global' &&
                  refreshedRemoteRevoke &&
                  isErrorCode(refreshError, 'auth_sign_out_timeout')
                ) {
                  // Bound the caller but keep the cross-tab lock until the
                  // uncancellable server-wide retry settles. Otherwise an old
                  // request could revoke a fresh same-account login that wins
                  // immediately after our visible timeout.
                  rejectVisible(refreshError);
                  const lateGlobalRevokeSucceeded = await refreshedRemoteRevoke
                    .then(() => true)
                    .catch(() => false);
                  if (lateGlobalRevokeSucceeded) {
                    partialGlobalFailure = undefined;
                  }
                }
                // Offline/local logout must still remove this profile's copy.
                // Any late refresh/revoke completion is network-only and cannot
                // touch the persisted session or a subsequent login.
              }
            }
            // A local revoke targets only the old refresh token and is
            // network-only. Late completion cannot mutate browser storage.
          }
        } else if (scope === 'global') {
          rejectVisible(new Error('remote_session_token_unavailable'));
          return;
        }

        if (!this.externalAccessToken && accessToken && signedOutUserId) {
          // Older dashboard builds do not take the app-owned auth lock around
          // signInWithPassword. Re-check immediately before deletion so such a
          // tab cannot save a fresh account/session during the network revoke
          // window and have this newer build erase it afterwards.
          const currentRaw = await auth.storage!.getItem(auth.storageKey!);
          const currentAccessToken = readPersistedAccessToken(currentRaw);
          const currentIdentity = readAuthTokenIdentity(currentAccessToken);
          const samePersistedSession =
            currentIdentity.userId === signedOutUserId &&
            (signedOutSessionGeneration !== null
              ? currentIdentity.sessionGeneration === signedOutSessionGeneration
              : currentAccessToken === accessToken);
          if (!samePersistedSession) {
            rejectVisible(new Error('auth_session_changed'));
            return;
          }
        }

        const localClear = this.clearPersistedSessionLocallyUnlocked(auth);
        void withTimeout(
          localClear,
          AUTH_LOCAL_SESSION_CLEAR_TIMEOUT_MS,
          'local_session_clear_timeout',
        ).catch(rejectVisible);
        // The public timeout never releases exclusivity: a later login queues
        // until remove+confirm actually settles.
        await localClear;
        localSessionCleared = true;
      }),
    );
    void background.then(() => {
      if (!localSessionCleared) return;
      // Raw remove+confirm deliberately bypasses GoTrue.signOut(), so bridge
      // the IAuthService state contract ourselves after the storage lock has
      // been released. Per-subscriber snapshots suppress a prior GoTrue
      // SIGNED_OUT from an in-flight authoritative failure.
      this.notifyAuthStateSubscribers(null, {
        signedOutUserId,
        signedOutSessionGeneration,
      });
      const result: AuthLogoutResult = {
        userId: signedOutUserId,
        sessionGeneration: signedOutSessionGeneration,
      };
      if (partialGlobalFailure !== undefined) {
        rejectVisible(
          Object.assign(new Error('global_session_revoke_unconfirmed'), {
            cause: partialGlobalFailure,
            localSessionCleared: true as const,
            signedOutUserId,
            signedOutSessionGeneration,
          }),
        );
        return;
      }
      resolveVisibleResult(result);
    }, rejectVisible);
    return visibleOutcome;
  }

  /**
   * GoTrueClient.signOut() performs the network request and then mutates the
   * primary client's shared storage. Racing that method against a timeout can
   * create a late "zombie" removal that deletes a newer login. The documented
   * admin signOut(jwt, scope) primitive is network-only, so late completion is
   * harmless to local state.
   */
  private async revokeSessionRemotely(
    accessToken: string,
    scope: AuthLogoutScope,
  ): Promise<void> {
    const { error } = await this.authSupabase.auth.admin.signOut(
      accessToken,
      scope,
    );
    if (error) throw error;
  }

  private async refreshAccessTokenForLogout(
    refreshToken: string,
  ): Promise<string | null> {
    const auth = this.getAuthStorageInternals();
    if (!auth._refreshAccessToken) return null;
    const { data, error } = await auth._refreshAccessToken(refreshToken);
    if (error) throw error;
    const accessToken = data.session?.access_token;
    return typeof accessToken === 'string' && accessToken.length > 0
      ? accessToken
      : null;
  }

  async clearLocalSession(
    options?: AuthLocalSessionClearOptions,
  ): Promise<void> {
    const auth = this.getAuthStorageInternals();
    if (!auth.storage || !auth.storageKey) {
      throw new Error('local_session_storage_unavailable');
    }

    const clearOperation = enqueueSessionStorageOperation(auth.storage, () =>
      runWithAuthStorageLock(auth, async () => {
        if (options) {
          // This comparison is intentionally inside the SAME Web Lock as the
          // removal and immediately precedes it. A same-account re-login that
          // won the lock has a new server session_id, so an older queued tab
          // becomes a no-op instead of erasing the fresh session.
          const raw = await auth.storage!.getItem(auth.storageKey!);
          const currentGeneration = readPersistedSessionGeneration(raw);
          if (
            options.expectedSessionGeneration === null ||
            currentGeneration !== options.expectedSessionGeneration
          ) {
            return;
          }
        }
        await this.clearPersistedSessionLocallyUnlocked(auth);
      }),
    );

    // Bound the caller, not the operation. The retained fence above keeps an
    // unfinished native-storage removal ahead of every later login/refresh,
    // eliminating the classic timeout -> login B -> late delete race.
    await withTimeout(
      clearOperation,
      AUTH_LOCAL_SESSION_CLEAR_TIMEOUT_MS,
      'local_session_clear_timeout',
    );
  }

  private async clearPersistedSessionLocallyUnlocked(
    auth: AuthStorageInternals,
  ): Promise<void> {
    if (!auth.storage || !auth.storageKey) {
      throw new Error('local_session_storage_unavailable');
    }
    // Stop future ticks while holding the same storage lock used by
    // _callRefreshToken. An already-running refresh finishes before this
    // callback and its late _saveSession is therefore removed below.
    await this.authSupabase.auth.stopAutoRefresh();

    const primaryKeys = [
      auth.storageKey,
      `${auth.storageKey}-code-verifier`,
      `${auth.storageKey}-user`,
    ];
    await removeAndConfirm(auth.storage, primaryKeys);
    if (auth.userStorage && auth.userStorage !== auth.storage) {
      await removeAndConfirm(auth.userStorage, [`${auth.storageKey}-user`]);
    }
  }

  private getAuthStorageInternals(): AuthStorageInternals {
    return this.authSupabase.auth as unknown as AuthStorageInternals;
  }

  private runAfterSessionStorageQuiescence<T>(
    operation: () => Promise<T>,
  ): Promise<T> {
    const auth = this.getAuthStorageInternals();
    if (!auth.storage) return operation();
    return enqueueSessionStorageOperation(auth.storage, operation);
  }

  private runSessionEstablishingOperation<T>(
    operation: () => Promise<T>,
  ): Promise<T> {
    const auth = this.getAuthStorageInternals();
    if (!auth.storage) return operation();
    // signInWithPassword/signUp/verifyOtp persist via _saveSession without
    // acquiring GoTrue's lock themselves. Take that exact cross-tab lock here
    // so a login in tab B cannot overtake tab A's slow async removal.
    return enqueueSessionStorageOperation(auth.storage, () =>
      runWithAuthStorageLock(auth, operation),
    );
  }

  async getAccessToken(): Promise<string | null> {
    const sessionTokens = await this.getSessionTokens();
    return sessionTokens?.accessToken ?? null;
  }

  async getSessionTokens(): Promise<AuthSessionTokens | null> {
    return this.runAfterSessionStorageQuiescence(() =>
      this.getSessionTokensAfterStorageQuiescence(),
    );
  }

  private async getSessionTokensAfterStorageQuiescence(): Promise<AuthSessionTokens | null> {
    if (this.externalAccessToken) {
      return {
        accessToken: this.externalAccessToken,
        refreshToken: null,
        expiresAt: null,
      };
    }

    // Supabase will throw AuthApiError when the persisted session has a
    // refresh token that's no longer valid (deleted, expired). Treat that
    // as "not signed in" so the app shows the login screen instead of
    // surfacing a red console error on every cold start.
    try {
      const { data } = await this.authSupabase.auth.getSession();
      const session = data.session;
      if (!session) return null;
      const expiresAt = session.expires_at ?? null;
      const refreshToken = session.refresh_token;
      const shouldRefresh =
        Boolean(refreshToken) &&
        expiresAt !== null &&
        expiresAt * 1000 <= Date.now() + TOKEN_REFRESH_WINDOW_MS;
      if (!shouldRefresh) {
        return {
          accessToken: session.access_token,
          refreshToken: refreshToken ?? null,
          expiresAt,
        };
      }

      const { data: refreshed, error } =
        await this.authSupabase.auth.refreshSession({
          refresh_token: refreshToken,
        });
      if (error) throw error;
      const nextSession = refreshed.session ?? session;
      return {
        accessToken: nextSession.access_token,
        refreshToken: nextSession.refresh_token ?? null,
        expiresAt: nextSession.expires_at ?? null,
      };
    } catch (err) {
      if (isInvalidRefreshTokenError(err)) {
        // auth-js removes an invalid persisted refresh session while holding
        // its own lock. Calling signOut() here would add a global /logout
        // request after the authoritative failure and could target a newer
        // session established while this stale caller was still unwinding.
        return null;
      }
      throw err;
    }
  }

  async acceptPlatformAdminTenantViewHandoff(
    token: string,
  ): Promise<PlatformAdminTenantViewAccess> {
    if (this.externalAccessToken) {
      return this.consumePlatformAdminTenantView(token);
    }

    const { data: sessionData } = await this.runAfterSessionStorageQuiescence(
      () =>
        this.authSupabase.auth.getSession().catch(() => ({
          data: { session: null },
        })),
    );
    if (sessionData.session?.access_token) {
      try {
        return await this.consumePlatformAdminTenantView(token);
      } catch {
        // Fall through to the edge SSO exchange. This covers dashboard-origin
        // opens where the local session is absent or stale while keeping the
        // same-origin admin flow off URL-borne JWTs.
      }
    }

    const { data, error } = await this.supabase.functions.invoke(
      'platform-admin-handoff',
      {
        body: { token },
      },
    );
    if (error) throw error;

    const payload = asRecord(data);
    const email = readString(payload.email);
    const tokenHash = readString(payload.tokenHash);
    const tenantId = readString(payload.tenantId);
    const expiresAt = readString(payload.expiresAt);
    const viewExpiresAt = readString(payload.viewExpiresAt);

    if (!email || !tokenHash || !tenantId || !expiresAt || !viewExpiresAt) {
      throw new Error('Invalid admin handoff response.');
    }

    // token_hash flow takes ONLY { token_hash, type } — passing `email` too
    // makes GoTrue reject with 400 "Only the token_hash and type should be
    // provided".
    const { error: verifyError } = await this.runSessionEstablishingOperation(
      async () => {
        const result = await this.authSupabase.auth.verifyOtp({
          type: 'magiclink',
          token_hash: tokenHash,
        });
        if (!result.error) await this.authSupabase.auth.startAutoRefresh();
        return result;
      },
    );
    if (verifyError) throw verifyError;

    return { tenantId, expiresAt, viewExpiresAt };
  }

  private async consumePlatformAdminTenantView(
    token: string,
  ): Promise<PlatformAdminTenantViewAccess> {
    // Not in the generated Database types yet — cast the call (same pattern as
    // createProPlatformHandoff below).
    const rpc = this.supabase.rpc as unknown as (
      fn: string,
      args: Record<string, unknown>,
    ) => Promise<{ data: unknown; error: { message: string } | null }>;
    const { data, error } = await rpc.call(
      this.supabase,
      'platform_admin_consume_tenant_view',
      {
        p_token: token,
      },
    );
    if (error) throw error;
    return mapTenantViewAccess(data);
  }

  async createStorefrontEditorHandoff(): Promise<StorefrontEditorHandoff | null> {
    const { data, error } = await this.supabase.rpc(
      'create_storefront_editor_handoff',
    );
    if (error) throw error;
    const payload = asRecord(data);
    const token = readString(payload.token);
    const expiresAt = readString(payload.expiresAt);
    if (!token || !expiresAt) return null;
    return { token, expiresAt };
  }

  async exchangeStorefrontEditorHandoff(
    token: string,
  ): Promise<AuthSessionTokens | null> {
    const { data, error } = await this.supabase.functions.invoke(
      'storefront-editor-handoff',
      { body: { token } },
    );
    if (error) throw error;

    const payload = asRecord(data);
    const email = readString(payload.email);
    const tokenHash = readString(payload.tokenHash);
    if (!email || !tokenHash) {
      throw new Error('Invalid storefront editor handoff response.');
    }

    // verifyOtp on the headerless auth client (persistSession is off for the
    // public storefront, so this mints the session without polluting the anon
    // browsing context) — we just read the tokens back out of the response.
    // token_hash flow takes ONLY { token_hash, type }; adding `email` makes
    // GoTrue reject with 400 "Only the token_hash and type should be provided".
    const { data: verified, error: verifyError } =
      await this.runSessionEstablishingOperation(async () => {
        const result = await this.authSupabase.auth.verifyOtp({
          type: 'magiclink',
          token_hash: tokenHash,
        });
        if (!result.error) await this.authSupabase.auth.startAutoRefresh();
        return result;
      });
    if (verifyError) throw verifyError;

    const session = verified.session;
    if (!session) {
      throw new Error('Storefront editor handoff did not yield a session.');
    }
    return {
      accessToken: session.access_token,
      refreshToken: session.refresh_token ?? null,
      expiresAt: session.expires_at ?? null,
    };
  }

  async createProPlatformHandoff(input: {
    kind: ProPlatformHandoffKind;
    proCustomerId?: string | null;
  }): Promise<ProPlatformHandoff | null> {
    // The new RPC isn't in the generated Database types yet — cast the call
    // (same pattern used elsewhere in the adapter for fresh RPCs).
    const rpc = this.supabase.rpc as unknown as (
      fn: string,
      args: Record<string, unknown>,
    ) => Promise<{ data: unknown; error: { message: string } | null }>;
    const { data, error } = await rpc.call(
      this.supabase,
      'create_pro_platform_handoff',
      {
        p_kind: input.kind,
        p_pro_customer_id: input.proCustomerId ?? null,
      },
    );
    if (error) throw error;
    const payload = asRecord(data);
    const token = readString(payload.token);
    const kind = readString(payload.kind);
    if (!token) return null;
    return {
      token,
      kind:
        kind === 'admin_preview' || kind === 'impersonate' ? kind : input.kind,
    };
  }

  async exchangeProPlatformHandoff(
    token: string,
  ): Promise<ProPlatformPreviewSession | null> {
    const { data, error } = await this.supabase.functions.invoke(
      'pro-platform-handoff',
      {
        body: { token },
      },
    );
    if (error) throw error;

    const payload = asRecord(data);
    const email = readString(payload.email);
    const tokenHash = readString(payload.tokenHash);
    if (!email || !tokenHash) {
      throw new Error('Invalid pro platform handoff response.');
    }

    // verifyOtp on the headerless client establishes the (persisted) session;
    // in erp-pro both clients are the same instance, so ProAuthProvider's
    // onAuthStateChange picks it up immediately.
    //
    // IMPORTANT: the token_hash flow takes ONLY { token_hash, type }. Passing
    // `email` as well makes GoTrue reject with 400 "Only the token_hash and
    // type should be provided" (the user is identified from the hash itself).
    const { error: verifyError } = await this.runSessionEstablishingOperation(
      async () => {
        const result = await this.authSupabase.auth.verifyOtp({
          type: 'magiclink',
          token_hash: tokenHash,
        });
        if (!result.error) await this.authSupabase.auth.startAutoRefresh();
        return result;
      },
    );
    if (verifyError) throw verifyError;

    const kind = readString(payload.kind);
    return {
      kind: kind === 'admin_preview' ? 'admin_preview' : 'impersonate',
      targetName: readString(payload.targetName),
    };
  }

  /**
   * Who is signed in according to the LOCALLY persisted session — no network.
   *
   * Why this exists: an access token lives ~1h, so any desktop cold boot more
   * than an hour after the last use starts with an expired token. Offline,
   * supabase-js cannot refresh it: `__loadSession()` calls `_callRefreshToken()`,
   * the fetch throws a *retryable* error, and `getSession()`/`getUser()` answer
   * with `user: null`. That is indistinguishable from a real sign-out at the
   * call site — and treating it as one bounced offline users to /login and (via
   * the boot hint → cache buster) deleted their whole offline mirror.
   *
   * Crucially, supabase only deletes the stored session for AUTHORITATIVE
   * failures (missing/invalid refresh token); a retryable network failure leaves
   * the session JSON on disk. So we read it back to recover the identity.
   *
   * This identity is UI-only. Every server read still needs a valid JWT and is
   * enforced by RLS, and the desktop subscription gate runs off its own vault
   * checkpoint — so a tampered session file yields a rendered shell with no
   * data, never elevated access.
   */
  private async readPersistedUser(): Promise<AuthUser | null> {
    try {
      // `storage` / `storageKey` are GoTrueClient instance properties (set from
      // the options we pass in factory.ts). Not in the public types, hence the cast.
      const auth = this.authSupabase.auth as unknown as {
        storage?: { getItem(key: string): unknown };
        storageKey?: string;
      };
      const storage = auth.storage;
      const storageKey = auth.storageKey;
      if (!storage || !storageKey) return null;

      const raw = await storage.getItem(storageKey);
      if (typeof raw !== 'string' || !raw) return null;
      const parsed = JSON.parse(raw) as {
        user?: SupabaseStoredUser;
        currentSession?: { user?: SupabaseStoredUser };
      } | null;
      // v2 stores the session object directly; older shapes nest it.
      const user = parsed?.user ?? parsed?.currentSession?.user;
      if (!user?.id || !user.email) return null;
      return {
        id: user.id,
        email: user.email,
        role: extractRole(user.user_metadata),
        displayName: extractDisplayName(user.user_metadata),
        phone: extractPhone(user.user_metadata),
      };
    } catch {
      // Corrupt/unreadable session file — treat as "no local identity".
      return null;
    }
  }

  async getCurrentUser(): Promise<AuthUser | null> {
    return this.runAfterSessionStorageQuiescence(() =>
      this.getCurrentUserAfterStorageQuiescence(),
    );
  }

  private async getCurrentUserAfterStorageQuiescence(): Promise<AuthUser | null> {
    try {
      if (this.externalAccessToken) {
        const { data, error } = await this.authSupabase.auth.getUser(
          this.externalAccessToken,
        );
        if (error) throw error;
        if (!data.user) return null;
        return {
          id: data.user.id,
          email: data.user.email!,
          role: extractRole(data.user.user_metadata),
          displayName: extractDisplayName(data.user.user_metadata),
          phone: extractPhone(data.user.user_metadata),
        };
      }

      // A desktop cold boot can start with an expired access token and no
      // network. GoTrue retries refresh for tens of seconds; waiting for that
      // retry made the router render /login after the five-second boot guard,
      // even though the durable session was still intact. Restore the local
      // identity immediately on the explicitly marked native storage. GoTrue's
      // auth observer continues authoritative validation in the background and
      // still publishes a real invalid-token/revocation sign-out.
      if (this.supportsOfflineSessionRestore) {
        const local = await this.readPersistedUser();
        if (local) return local;
      }

      const { data, error } = await this.authSupabase.auth.getUser();
      // getUser() returns an error object (rather than throwing) when the
      // refresh token is missing/invalid. Same treatment: clear and bail.
      // (isInvalidRefreshTokenError also covers AuthSessionMissingError, i.e.
      // a genuinely absent session — supabase has already wiped it by then.)
      if (error && isInvalidRefreshTokenError(error)) {
        return null;
      }
      if (error) {
        // We could not VALIDATE the session over the network (offline, DNS
        // dead, server down) — an AuthRetryableFetchError. This is NOT a
        // sign-out. Recover the locally persisted identity so an offline
        // desktop boot renders the app instead of the login screen.
        const local = await this.readPersistedUser();
        if (local) return local;
        throw error;
      }
      if (!data.user) return null;
      return {
        id: data.user.id,
        email: data.user.email!,
        role: extractRole(data.user.user_metadata),
        displayName: extractDisplayName(data.user.user_metadata),
        phone: extractPhone(data.user.user_metadata),
        pendingEmail: extractPendingEmail(data.user),
      };
    } catch (err) {
      if (isInvalidRefreshTokenError(err)) {
        return null;
      }
      const local = await this.readPersistedUser();
      if (local) return local;
      throw err;
    }
  }

  onAuthStateChange(
    callback: (
      user: AuthUser | null,
      metadata?: AuthStateChangeMetadata,
    ) => void,
  ): () => void {
    if (this.externalAccessToken) {
      return () => undefined;
    }

    const subscriber: AuthStateSubscriber = { callback };
    this.authStateSubscribers.add(subscriber);
    const deliver = (
      user: AuthUser | null,
      metadata?: AuthStateChangeMetadata,
    ) => this.deliverAuthState(subscriber, user, metadata);
    const { data } = this.authSupabase.auth.onAuthStateChange(
      (event, session) => {
        if (session?.user) {
          deliver(
            {
              id: session.user.id,
              email: session.user.email!,
              role: extractRole(session.user.user_metadata),
              displayName: extractDisplayName(session.user.user_metadata),
              phone: extractPhone(session.user.user_metadata),
              // `updateUser({ email })` émet un USER_UPDATED : c'est par ici que
              // l'écran Sécurité apprend qu'un changement est en attente, sans
              // avoir à re-interroger le serveur.
              pendingEmail: extractPendingEmail(session.user),
            },
            {
              sessionGeneration: readSessionGenerationFromAccessToken(
                session.access_token,
              ),
            },
          );
          return;
        }
        // GoTrue normally emits SIGNED_OUT only after removing its stored
        // session. A retryable refresh failure has nevertheless been observed
        // to emit the same event during an offline desktop navigation while
        // leaving the durable session intact. Treat the storage boundary as
        // the authority: a persisted identity means the account was not
        // actually signed out and the offline shell must remain available.
        if (event === 'SIGNED_OUT') {
          if (!this.supportsOfflineSessionRestore) {
            deliver(null);
            return;
          }
          void this.runAfterSessionStorageQuiescence(() =>
            this.readPersistedUser(),
          )
            .then((persisted) => deliver(persisted))
            .catch(() => deliver(null));
          return;
        }
        // Any other null-session event — notably INITIAL_SESSION on an OFFLINE
        // cold boot whose expired token could not be refreshed — is not proof of
        // a logout: supabase keeps the session file on retryable failures. Fall
        // back to the persisted identity so the app boots offline. Returns null
        // (→ real logout) when no session file exists.
        void this.readPersistedUser()
          .then(deliver)
          .catch(() => deliver(null));
      },
    );
    return () => {
      this.authStateSubscribers.delete(subscriber);
      data.subscription.unsubscribe();
    };
  }

  private deliverAuthState(
    subscriber: AuthStateSubscriber,
    user: AuthUser | null,
    metadata?: AuthStateChangeMetadata,
  ): void {
    if (!this.authStateSubscribers.has(subscriber)) return;
    const snapshot = authUserSnapshot(user, metadata?.sessionGeneration);
    if (subscriber.lastSnapshot === snapshot) return;
    subscriber.lastSnapshot = snapshot;
    subscriber.callback(user, metadata);
  }

  private notifyAuthStateSubscribers(
    user: AuthUser | null,
    metadata?: AuthStateChangeMetadata,
  ): void {
    for (const subscriber of [...this.authStateSubscribers]) {
      try {
        this.deliverAuthState(subscriber, user, metadata);
      } catch (error) {
        // Observer failures must not turn a confirmed local logout back into a
        // rejected operation or prevent the remaining shared consumers from
        // seeing the signed-out boundary.
        console.error('[auth] auth-state subscriber failed', error);
      }
    }
  }
}

/** Shape of the `user` object inside supabase's persisted session JSON. */
interface SupabaseStoredUser {
  id?: string;
  email?: string;
  user_metadata?: Record<string, unknown> | null;
}

function withoutGlobalHeaders(
  options: SupabaseClientOptions<'public'> | undefined,
): SupabaseClientOptions<'public'> | undefined {
  if (!options?.global?.headers) return options;
  const { global, ...rest } = options;
  const { headers: _headers, ...globalRest } = global;
  return Object.keys(globalRest).length > 0
    ? { ...rest, global: globalRest }
    : rest;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function mapTenantViewAccess(value: unknown): PlatformAdminTenantViewAccess {
  const payload = asRecord(value);
  const tenantId = readString(payload.tenantId);
  const expiresAt = readString(payload.expiresAt);
  const viewExpiresAt = readString(payload.viewExpiresAt);

  if (!tenantId || !expiresAt || !viewExpiresAt) {
    throw new Error('Invalid admin handoff response.');
  }

  return { tenantId, expiresAt, viewExpiresAt };
}

function extractRole(
  meta: Record<string, unknown> | null | undefined,
): string | null {
  if (!meta) return null;
  const r = meta['role'];
  return typeof r === 'string' ? r : null;
}

/** Téléphone de contact posé par `signup()` (user_metadata.phone). */
function extractPhone(
  meta: Record<string, unknown> | null | undefined,
): string | null {
  if (!meta) return null;
  return readString(meta['phone']);
}

/** Best-effort human name from user_metadata. Checks the fields our own
 *  signups write (first_name/last_name — see signupAsPro) plus the common
 *  Supabase/OAuth conventions (full_name, name, display_name). */
function extractDisplayName(
  meta: Record<string, unknown> | null | undefined,
): string | null {
  if (!meta) return null;
  const first = readString(meta['first_name']);
  const last = readString(meta['last_name']);
  if (first) return last ? `${first} ${last}` : first;
  return (
    readString(meta['full_name']) ??
    readString(meta['display_name']) ??
    readString(meta['name'])
  );
}

/**
 * Adresse en attente de confirmation après `updateUser({ email })`.
 *
 * GoTrue la range dans `new_email` et ne bascule `email` qu'une fois les deux
 * liens ouverts. Le champ n'est pas typé sur toutes les versions du SDK, d'où
 * la lecture défensive : une version qui ne l'exposerait pas rend simplement
 * `null` (l'écran n'affiche alors pas la bannière d'attente).
 */
function extractPendingEmail(user: unknown): string | null {
  if (!user || typeof user !== 'object') return null;
  return readString((user as Record<string, unknown>)['new_email']) ?? null;
}

/** UI language cached by i18next (key `i18nextLng`), narrowed to a supported
 *  locale. Used to stamp `user_metadata.locale` at signup so auth emails can be
 *  localised. Guarded — returns undefined in non-browser / private-mode cases. */
function detectLocale(): 'fr' | 'ar' | 'en' | undefined {
  try {
    const raw = globalThis.localStorage?.getItem('i18nextLng') ?? '';
    const v = raw.toLowerCase().slice(0, 2);
    return v === 'fr' || v === 'ar' || v === 'en' ? v : undefined;
  } catch {
    return undefined;
  }
}

const STOREFRONT_SIGNUP_ERROR_CODES: ReadonlySet<string> =
  new Set<StorefrontSignupErrorCode>([
    'email_already_registered',
    'invalid_email',
    'weak_password',
    'too_many_signups',
    'unknown_storefront',
    'signup_failed',
  ]);

/** Turn a functions.invoke failure into an Error carrying a known
 *  `StorefrontSignupErrorCode`. The edge function answers `{ error: "<code>" }`;
 *  anything else (network drop, 5xx with no body, an unrecognised code)
 *  collapses to `signup_failed` so the shop never renders a raw backend string
 *  at a customer. */
async function toStorefrontSignupError(error: unknown): Promise<Error> {
  let code: StorefrontSignupErrorCode = 'signup_failed';
  const context = (error as { context?: unknown } | null)?.context;
  if (context instanceof Response) {
    try {
      const body = (await context.clone().json()) as { error?: unknown };
      if (
        typeof body.error === 'string' &&
        STOREFRONT_SIGNUP_ERROR_CODES.has(body.error)
      ) {
        code = body.error as StorefrontSignupErrorCode;
      }
    } catch {
      /* non-JSON body — keep the fallback */
    }
  }
  return Object.assign(new Error(code), { code });
}

const PRO_SIGNUP_ERROR_CODES: ReadonlySet<string> = new Set<ProSignupErrorCode>(
  [
    'email_already_registered',
    'invalid_email',
    'weak_password',
    'password_too_long',
    'too_many_signups',
    'unknown_tenant',
    'signup_failed',
  ],
);

/** Turn a functions.invoke failure into an Error carrying a known
 *  `ProSignupErrorCode`. Same contract as the storefront variant: the edge
 *  function answers `{ error: "<code>" }`, anything else collapses to
 *  `signup_failed` so the portal never renders a raw backend string. */
async function toProSignupError(error: unknown): Promise<Error> {
  let code: ProSignupErrorCode = 'signup_failed';
  const context = (error as { context?: unknown } | null)?.context;
  if (context instanceof Response) {
    try {
      const body = (await context.clone().json()) as { error?: unknown };
      if (
        typeof body.error === 'string' &&
        PRO_SIGNUP_ERROR_CODES.has(body.error)
      ) {
        code = body.error as ProSignupErrorCode;
      }
    } catch {
      /* non-JSON body — keep the fallback */
    }
  }
  return Object.assign(new Error(code), { code });
}

/** Recognises the family of errors Supabase raises when the persisted refresh
 *  token can no longer be used (deleted server-side, expired, or never
 *  existed). All variants share the "Invalid Refresh Token" / "refresh_token"
 *  signature in either `name`, `code`, or `message`. */
function isInvalidRefreshTokenError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as {
    name?: string;
    code?: string;
    message?: string;
    status?: number;
  };
  const name = e.name ?? '';
  const code = e.code ?? '';
  const message = e.message ?? '';
  if (name === 'AuthSessionMissingError') return true;
  if (code === 'refresh_token_not_found' || code === 'invalid_refresh_token')
    return true;
  if (
    /refresh\s*token/i.test(message) &&
    /(invalid|not\s*found|missing)/i.test(message)
  )
    return true;
  return false;
}
